import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane, faCheckCircle,
  faExclamationTriangle, faTrash, faRotateRight,
  faClock, faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import ModalHeader from "./ModalHeader";
import api from "../services/api";
import styles from "./InviteStudentModal.module.css";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

const STATUS_LABEL = {
  pending:  { label: "Pendente",  color: "amber" },
  accepted: { label: "Aceito",    color: "green"  },
  expired:  { label: "Expirado",  color: "muted"  },
};

function InviteRow({ inv, onCancel, confirmingId, onConfirmDelete, onAbortDelete }) {
  const meta = STATUS_LABEL[inv.status] || STATUS_LABEL.expired;
  const isConfirming = confirmingId === inv.id;
  return (
    <div className={styles.inviteRow}>
      <div className={styles.inviteInfo}>
        <span className={styles.inviteName}>{inv.name}</span>
        <span className={styles.inviteEmail}>{inv.email}</span>
      </div>
      {isConfirming ? (
        <div className={styles.deleteConfirm}>
          <span className={styles.deleteConfirmText}>Cancelar convite?</span>
          <button className={styles.deleteConfirmYes} onClick={onConfirmDelete} title="Confirmar">
            Sim
          </button>
          <button className={styles.deleteConfirmNo} onClick={onAbortDelete} title="Voltar">
            Não
          </button>
        </div>
      ) : (
        <>
          <span className={`${styles.statusBadge} ${styles[`status_${meta.color}`]}`}>
            {meta.label}
          </span>
          {inv.status === "pending" && (
            <button
              className={styles.cancelBtn}
              onClick={() => onCancel(inv.id)}
              title="Cancelar convite"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default function InviteProfessorModal({ open, onClose, initialKind = "professor" }) {
  const [name, setName]           = useState("");
  useBodyScrollLock(open);
  const [email, setEmail]         = useState("");
  // "professor" = professor autônomo (como sempre foi); "academy" = cria a
  // academia e o convidado entra como dono (ver app/models/organization.py).
  const [kind, setKind]           = useState("professor");
  const [orgName, setOrgName]     = useState("");
  const [sending, setSending]     = useState(false);
  const [success, setSuccess]     = useState(null);
  const [error, setError]         = useState(null);
  const [invites, setInvites]     = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [cancelConfirmId, setCancelConfirmId] = useState(null);

  const loadInvites = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await api.get("/admin/professors/invites");
      setInvites(res.data);
    } catch (_) {
      // silencioso
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (open) { loadInvites(); }
  }, [open, loadInvites]);

  // Cada abertura começa no tipo pedido por quem abriu (atalho "Convidar
  // professor" = autônomo; "Nova academia" na lista de academias = Academia).
  useEffect(() => {
    if (open) setKind(initialKind);
  }, [open, initialKind]);

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    if (kind === "academy" && orgName.trim().length < 2) return;
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.post("/admin/professors/invite", {
        name: name.trim(),
        email: email.trim(),
        ...(kind === "academy" ? { organization_name: orgName.trim() } : {}),
      });
      setSuccess(res.data.message || "Convite enviado por email!");
      setName("");
      setEmail("");
      setOrgName("");
      loadInvites();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object") {
        setError(detail.message || "Erro ao enviar convite.");
      } else {
        setError(detail || "Erro ao enviar convite.");
      }
    } finally {
      setSending(false);
    }
  };

  const handleCancel = (inviteId) => {
    setCancelConfirmId(inviteId);
  };

  const handleCancelConfirmed = async () => {
    const inviteId = cancelConfirmId;
    setCancelConfirmId(null);
    try {
      await api.delete(`/admin/professors/invites/${inviteId}`);
      loadInvites();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "object" ? detail.message : detail || "Erro ao cancelar convite.");
    }
  };

  if (!open) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <ModalHeader onClose={onClose} />
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>Convidar Professor</h2>
          <p className={styles.sub}>Envie um convite por email para o professor entrar no TreinoNexo</p>
        </div>

        <div className={styles.body}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.field}>
              <span className={styles.label} id="inv-prof-kind-label">Tipo de conta</span>
              <div className={styles.tabs} role="group" aria-labelledby="inv-prof-kind-label">
                <button
                  type="button"
                  className={`${styles.tabBtn} ${kind === "professor" ? styles.tabBtnActive : ""}`}
                  aria-pressed={kind === "professor"}
                  onClick={() => setKind("professor")}
                >
                  Professor autônomo
                </button>
                <button
                  type="button"
                  className={`${styles.tabBtn} ${kind === "academy" ? styles.tabBtnActive : ""}`}
                  aria-pressed={kind === "academy"}
                  onClick={() => setKind("academy")}
                >
                  Academia
                </button>
              </div>
              {kind === "academy" && (
                <span className={styles.modeHint}>
                  Cria a academia e o convidado entra como dono. Depois, ele convida os professores pela tela Minha academia.
                  Se a academia já usa o TreinoNexo com um perfil de professor, converta esse perfil na edição do usuário.
                </span>
              )}
            </div>
            {kind === "academy" && (
              <div className={styles.field}>
                <label htmlFor="inv-prof-org-name" className={styles.label}>Nome da academia</label>
                <input
                  id="inv-prof-org-name"
                  className={styles.input}
                  type="text"
                  placeholder="Ex.: Academia Movimento"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  required
                  minLength={2}
                  maxLength={120}
                  autoComplete="off"
                />
              </div>
            )}
            <div className={styles.field}>
              <label htmlFor="inv-prof-name" className={styles.label}>{kind === "academy" ? "Nome do dono da academia" : "Nome do professor"}</label>
              <input
                id="inv-prof-name"
                className={styles.input}
                type="text"
                placeholder="Ex.: Maria Souza"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="off"
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="inv-prof-email" className={styles.label}>{kind === "academy" ? "Email do dono da academia" : "Email do professor"}</label>
              <input
                id="inv-prof-email"
                className={styles.input}
                type="email"
                placeholder="maria@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
              />
            </div>

            {success && (
              <div className={styles.feedbackSuccess}>
                <FontAwesomeIcon icon={faCheckCircle} /> {success}
              </div>
            )}
            {error && (
              <div className={styles.feedbackError}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
              </div>
            )}

            <button type="submit" className={styles.sendBtn} disabled={sending}>
              <FontAwesomeIcon icon={faPaperPlane} />
              {sending ? "Enviando…" : "Enviar convite"}
            </button>
          </form>

          <div className={styles.listSection}>
            <div className={styles.listHeader}>
              <span className={styles.listTitle}>
                <FontAwesomeIcon icon={faEnvelope} /> Convites enviados
              </span>
              <button className={styles.refreshBtn} onClick={loadInvites} title="Atualizar">
                <FontAwesomeIcon icon={faRotateRight} className={loadingList ? styles.spin : ""} />
              </button>
            </div>

            {invites.length === 0 ? (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faClock} />
                <span>Nenhum convite enviado ainda.</span>
              </div>
            ) : (
              <div className={styles.inviteList}>
                {invites.map((inv) => (
                  <InviteRow key={inv.id} inv={inv} onCancel={handleCancel} confirmingId={cancelConfirmId} onConfirmDelete={handleCancelConfirmed} onAbortDelete={() => setCancelConfirmId(null)} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
